/**
 * Exact decimal money utilities. Money is NEVER represented as a float.
 * We scale fixed-precision decimals to integer "units" (BigInt) at SCALE
 * decimal places and do all arithmetic on integers, matching the DB's
 * NUMERIC(20,4) columns. (Research: floats don't work for storing cents.)
 */

export const SCALE = 4;
const SCALE_FACTOR = 10n ** BigInt(SCALE);
// NUMERIC(20,4) → at most 16 integer digits, so |value| < 10^16, i.e. |units| < 10^20.
export const MAX_UNITS = 10n ** 20n;

/** Parse a decimal string/number into scaled BigInt units (4 dp). Throws on garbage/overflow. */
export function toUnits(value: string | number): bigint {
  const str = typeof value === 'number' ? numberToFixed(value) : value.trim();
  if (!/^-?\d+(\.\d+)?$/.test(str)) {
    throw new Error(`Invalid money value: ${JSON.stringify(value)}`);
  }
  const negative = str.startsWith('-');
  const [intPart, fracPartRaw = ''] = (negative ? str.slice(1) : str).split('.');
  if (fracPartRaw.length > SCALE) {
    // Reject more precision than we store rather than silently rounding.
    throw new Error(`Money value exceeds ${SCALE} decimal places: ${str}`);
  }
  const fracPart = fracPartRaw.padEnd(SCALE, '0');
  // ⚠️ WRITTEN FOR `noUncheckedIndexedAccess`, which the TILL's build has on and the API's does
  // not — ERP Phase 6 increment 9. This module is imported by BOTH, so it compiles under the
  // stricter of the two. The guard is not defensive noise: it says out loud what the line
  // already relied on.
  const units = BigInt(intPart ?? '0') * SCALE_FACTOR + BigInt(fracPart || '0');
  if (units >= MAX_UNITS) {
    throw new Error(`Money value exceeds the maximum supported magnitude: ${str}`);
  }
  return negative ? -units : units;
}

/** Render scaled BigInt units back to a fixed-precision decimal string (for NUMERIC columns). */
export function fromUnits(units: bigint): string {
  const negative = units < 0n;
  const abs = negative ? -units : units;
  const intPart = abs / SCALE_FACTOR;
  const fracPart = (abs % SCALE_FACTOR).toString().padStart(SCALE, '0');
  return `${negative ? '-' : ''}${intPart}.${fracPart}`;
}

function numberToFixed(n: number): string {
  if (!Number.isFinite(n)) throw new Error(`Invalid money number: ${n}`);
  const fixed = n.toFixed(SCALE);
  // Mirror the string path: reject precision loss rather than silently rounding a float.
  if (Number(fixed) !== n) {
    throw new Error(`Money number ${n} exceeds ${SCALE} decimal places`);
  }
  return fixed;
}

/** Round a (debit positive / credit negative) running total helper. */
export function signedUnits(direction: 'debit' | 'credit', units: bigint): bigint {
  return direction === 'debit' ? units : -units;
}

// ── FX rate (scale 10, matching the fx_rate NUMERIC(20,10) column) ────────────
const RATE_SCALE = 10;
const RATE_FACTOR = 10n ** BigInt(RATE_SCALE);

export function toRateUnits(value: string | number): bigint {
  const str = typeof value === 'number' ? value.toFixed(RATE_SCALE) : value.trim();
  if (!/^\d+(\.\d+)?$/.test(str)) throw new Error(`Invalid fx rate: ${JSON.stringify(value)}`);
  const [intPart, fracRaw = ''] = str.split('.');
  if (fracRaw.length > RATE_SCALE) throw new Error(`fx rate exceeds ${RATE_SCALE} dp: ${str}`);
  return BigInt(intPart ?? '0') * RATE_FACTOR + BigInt(fracRaw.padEnd(RATE_SCALE, '0') || '0');
}

export function divRoundHalfUp(num: bigint, den: bigint): bigint {
  const neg = num < 0n !== den < 0n;
  const a = num < 0n ? -num : num;
  const b = den < 0n ? -den : den;
  const q = a / b;
  const r = a % b;
  const rounded = r * 2n >= b ? q + 1n : q;
  return neg ? -rounded : rounded;
}

/**
 * The TAX-EXCLUSIVE unit value a statutory form's unit-value column wants — ERP Phase 4.
 *
 * ## 🔴 THE 6.3 CONTRADICTED ITSELF ON ITS OWN FACE, FOR EVERY INCLUSIVE-PRICED LINE
 *
 * বিধি ৪০'s table puts unit value in column 5, total value in column 6 and value-including-tax in
 * column 10. The print fed `unit_price` into column 5 and `line_subtotal` into column 6 — and on an
 * INCLUSIVE code those are measured differently: a line of 2 × ৳115 printed a unit value of 115.00
 * beside a total value of 200.00, so **column 5 × column 4 came to 230 while column 6 said 200**.
 * Anyone checking the arithmetic across the row — which is the first thing an audit does — finds a
 * tax invoice that disagrees with itself.
 *
 * ⚠️ IT WAS INVISIBLE ON AN EXCLUSIVE CODE, where the two are the same number, and that is most of
 * the documents this product has printed. §4's retail pricing is inclusive by design, so the till
 * would have made it the normal case.
 *
 * 🔧 DERIVED FROM THE SUBTOTAL RATHER THAN THE PRICE, so the column that must reconcile is the one
 * it is computed from. A zero quantity has no unit value to state and returns zero rather than
 * dividing.
 */
export function unitValueOf(lineSubtotalUnits: bigint, quantityUnits: bigint): bigint {
  if (quantityUnits === 0n) return 0n;
  return divRoundHalfUp(lineSubtotalUnits * SCALE_FACTOR, quantityUnits);
}

/** product (4 dp units) = a (4 dp units) × b (4 dp units), rounded half-up. e.g. qty × unitPrice. */
export function mulUnits(a: bigint, b: bigint): bigint {
  const result = divRoundHalfUp(a * b, SCALE_FACTOR);
  if (result >= MAX_UNITS || -result >= MAX_UNITS) {
    throw new Error('Product exceeds the maximum supported magnitude');
  }
  return result;
}

/** tax (4 dp units) = amount (4 dp units) × ratePercent%, rounded half-up. e.g. 15 → 15%. */
export function applyPercent(amountUnits: bigint, ratePercent: string | number): bigint {
  // rateUnits is at 4 dp; dividing by 10^6 removes the rate scale (10^4) and the /100 of percent.
  const rateUnits = toUnits(String(ratePercent));
  const result = divRoundHalfUp(amountUnits * rateUnits, 10n ** 6n);
  if (result >= MAX_UNITS || -result >= MAX_UNITS) {
    throw new Error('Tax amount exceeds the maximum supported magnitude');
  }
  return result;
}

/** base_amount (4 dp units) = amount (4 dp units) × fx_rate, rounded half-up. */
export function applyRate(amountUnits: bigint, rate: string | number): bigint {
  const result = divRoundHalfUp(amountUnits * toRateUnits(rate), RATE_FACTOR);
  if (result >= MAX_UNITS || -result >= MAX_UNITS) {
    throw new Error('Converted base amount exceeds the maximum supported magnitude');
  }
  return result;
}

/**
 * Split a TAX-INCLUSIVE amount into its net value and the tax inside it — Phase 8, increment 2.
 *
 * ## The inclusive amount is AUTHORITATIVE, and the tax is the remainder
 *
 * That is a deliberate choice and the increment's finding is why it had to be. The obvious property
 * to want is
 *
 *     exclusive + applyPercent(exclusive, rate) === inclusive
 *
 * and **it is not achievable**. `f(E) = E + round(E × rate)` is non-decreasing in E but SKIPS
 * values: at 15%, twenty-six of the first two hundred inclusive totals have no exclusive price at
 * all that maps back onto them. Ask for exactness there and the answer is that there is none.
 *
 * A shelf price is a promise to a customer, so the amount that must not move is the INCLUSIVE one.
 * This returns a net and a tax that sum to it EXACTLY, by construction, for every input — and the
 * tax is within one unit of what `applyPercent` would have charged on that net, which is the most
 * that can be true at once.
 *
 * ⚠️ So an inclusive line and an exclusive line at the same rate can differ by one unit in the tax.
 * That is not a defect to round away: it is the price having been fixed first.
 */
export function splitInclusive(
  inclusiveUnits: bigint,
  ratePercent: string | number,
): { exclusiveUnits: bigint; taxUnits: bigint } {
  const rateUnits = toUnits(String(ratePercent));
  if (rateUnits < 0n) throw new Error('A tax rate cannot be negative.');
  // net = inclusive ÷ (1 + rate). In units: inclusive × 10^6 ÷ (10^6 + rateUnits), where 10^6
  // carries the rate's own 10^4 scale and the /100 of a percent — the same factor `applyPercent`
  // divides by, so the two functions cannot drift apart in their reading of what a rate is.
  const denominator = 10n ** 6n + rateUnits;
  const exclusiveUnits = divRoundHalfUp(inclusiveUnits * 10n ** 6n, denominator);
  // 🔴 The remainder, not a second rounding. This is what makes the sum exact.
  return { exclusiveUnits, taxUnits: inclusiveUnits - exclusiveUnits };
}
